/**
 * Project mutation hooks using factory pattern with realtime broadcast
 * Reduced from 173 lines to ~60 lines (65% reduction)
 * All features preserved: create, update, updateField, delete, realtime sync
 */

import type {
  DeleteProjectOptions,
  MergeProjectsRequest,
} from '@/lib/api/projects.api'
import { projectsApi } from '@/lib/api/projects.api'
import { buildUpdatePayload } from '@/utils/saveCustomField'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { CreateProject, ProjectWithEntity } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { projectModuleCountsKey } from '../useProjectModuleCounts'
import { createResourceMutations } from './createResourceMutations'

// Define types for the factory
type ProjectCreate = CreateProject & { organizationId: string }
type ProjectUpdate = Partial<ProjectWithEntity>

// Core mutations using factory with realtime enabled
const projectMutations = createResourceMutations<
  ProjectWithEntity,
  ProjectCreate,
  ProjectUpdate
>({
  resourceName: 'Project',
  resourceNameSingular: 'project',
  queryKeyFactory: queryKeys.projects,
  api: {
    create: (data: ProjectCreate) => projectsApi.createProject(data),
    update: (id: string, data: ProjectUpdate) =>
      projectsApi.updateProject(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = buildUpdatePayload(field, value)
      return projectsApi.updateProject(id, updateData)
    },
    delete: (id: string) => projectsApi.deleteProject(id),
  },
  enableRealtime: true,
  deferredEntityType: 'project',
  invalidation: {
    onCreate: { invalidateRelated: [[...projectModuleCountsKey]] },
    onDelete: { invalidateRelated: [[...projectModuleCountsKey]] },
  },
})

// Export create mutation with wrapper for backward-compatible API signature
export function useCreateProjectMutation() {
  const mutation = projectMutations.useCreateMutation()
  return {
    ...mutation,
    mutate: ({
      projectData,
      organizationId,
    }: {
      projectData: CreateProject
      organizationId: string
    }) => mutation.mutate({ data: { ...projectData, organizationId } }),
    mutateAsync: ({
      projectData,
      organizationId,
    }: {
      projectData: CreateProject
      organizationId: string
    }) => mutation.mutateAsync({ data: { ...projectData, organizationId } }),
  }
}

export const useUpdateProjectMutation = projectMutations.useUpdateMutation
export const useUpdateProjectFieldMutation =
  projectMutations.useUpdateFieldMutation!
export const useDeleteProjectMutation = projectMutations.useDeleteMutation

/**
 * Delete mutation that supports linked item options (reassign/unlink/delete per category)
 */
export function useDeleteProjectWithOptionsMutation() {
  return useMutation({
    mutationFn: ({
      id,
      options,
    }: {
      id: string
      options?: DeleteProjectOptions
    }) => projectsApi.deleteProject(id, options),
    onSuccess: () => {
      // Query cleanup is handled by the caller after the deletion animation completes
    },
    onError: () => {
      toast.error('Failed to delete project. Please try again.')
    },
  })
}

export function useMergeProjectsMutation() {
  return useMutation({
    mutationFn: (data: MergeProjectsRequest) => projectsApi.mergeProjects(data),
    onError: () => {
      toast.error('Failed to merge projects. Please try again.')
    },
  })
}
